<template>
  <div>
    <span class="u-hidden-visually" id="datepicker-start-info">
      Insert start date as dd/mm/yyyy or click on date picker to choose a start date
    </span>

    <VueDatePicker
      v-model="startDate"
      ref="startDatePickerRef"
      :allowed-dates="allowedDates"
      :formats="formatConfig"
      :min-date="minDate"
      :max-date="maxDate"
      :time-config="timeConfig"
      auto-apply
      prevent-min-max-navigation
      arrow-navigation
      @update:model-value="onStartDateChange"
    >
      <template #dp-input="{ value, onInput, onBlur, onEnter, onTab }">
        <input
          class="dp__pointer dp__input_readonly dp__input dp__input_icon_pad dp__input_reg"
          :value="value"
          placeholder="Start date"
          aria-label="Start date"
          aria-describedby="datepicker-start-info"
          name="start-date"
          @input="onInput"
          @blur="onBlur"
          @keydown.enter.prevent="onEnter"
          @keydown.tab="onTab"
        />
        <svg
          class="dp__icon dp__input_icon dp__input_icons"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M29.333 8c0-2.208-1.792-4-4-4h-18.667c-2.208 0-4 1.792-4 4v18.667c0 2.208 1.792 4 4 4h18.667c2.208 0 4-1.792 4-4v-18.667zM26.667 8v18.667c0 0.736-0.597 1.333-1.333 1.333h-18.667c-0.736 0-1.333-0.597-1.333-1.333v-18.667c0-0.736 0.597-1.333 1.333-1.333h18.667c0.736 0 1.333 0.597 1.333 1.333z" />
          <path d="M20 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z" />
          <path d="M9.333 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z" />
          <path d="M4 14.667h24c0.736 0 1.333-0.597 1.333-1.333s-0.597-1.333-1.333-1.333h-24c-0.736 0-1.333 0.597-1.333 1.333s0.597 1.333 1.333 1.333z" />
        </svg>
      </template>
    </VueDatePicker>
  </div>

  <div>
     <span class="u-hidden-visually" id="datepicker-end-info">
        Insert end date as dd/mm/yyyy or click on date picker to choose an end date
      </span>
      <VueDatePicker
        v-model="endDate"
        ref="endDatePickerRef"
        :allowed-dates="allowedDates"
        :formats="formatConfig"
        :min-date="endMinDate"
        :max-date="maxDate"
        :time-config="timeConfig"
        auto-apply
        prevent-min-max-navigation
        arrow-navigation
        @update:model-value="onEndDateChange"
      >
      <template #dp-input="{ value, onInput, onBlur, onEnter, onTab }">
        <input
          class="dp__pointer dp__input_readonly dp__input dp__input_icon_pad dp__input_reg"
          :value="value"
          placeholder="End date"
          aria-label="End date"
          aria-describedby="datepicker-end-info"
          name="end-date"
          @input="onInput"
          @blur="onBlur"
          @keydown.enter.prevent="onEnter"
          @keydown.tab="onTab"
        />
        <svg
          class="dp__icon dp__input_icon dp__input_icons"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M29.333 8c0-2.208-1.792-4-4-4h-18.667c-2.208 0-4 1.792-4 4v18.667c0 2.208 1.792 4 4 4h18.667c2.208 0 4-1.792 4-4v-18.667zM26.667 8v18.667c0 0.736-0.597 1.333-1.333 1.333h-18.667c-0.736 0-1.333-0.597-1.333-1.333v-18.667c0-0.736 0.597-1.333 1.333-1.333h18.667c0.736 0 1.333 0.597 1.333 1.333z" />
          <path d="M20 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z" />
          <path d="M9.333 2.667v5.333c0 0.736 0.597 1.333 1.333 1.333s1.333-0.597 1.333-1.333v-5.333c0-0.736-0.597-1.333-1.333-1.333s-1.333 0.597-1.333 1.333z" />
          <path d="M4 14.667h24c0.736 0 1.333-0.597 1.333-1.333s-0.597-1.333-1.333-1.333h-24c-0.736 0-1.333 0.597-1.333 1.333s0.597 1.333 1.333 1.333z" />
        </svg>
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

/** Date states */
const startDate = ref(null);  
const endDate = ref(null);  

/** Picker refs */
const startDatePickerRef = ref(null)
const endDatePickerRef = ref(null)

/** Allowed dates */
const allowedDates = window.whatsOn?.availableDates?.map(d => new Date(d)) || [];
const minDate = allowedDates.length ? new Date(Math.min(...allowedDates)) : new Date();
const maxDate = allowedDates.length ? new Date(Math.max(...allowedDates)) : minDate;

/** Reactive end date minimum */
const endMinDate = computed(() => startDate.value ?? minDate);

/** DatePicker configuration */
const timeConfig = { enableTimePicker: false };
const formatConfig = { input: 'dd/MM/yyyy' };

/** Event handlers */

/**
 * Automatically open end date picker after selecting start date
 */
function onStartDateChange(date) {
  endDate.value = date;
  endDatePickerRef.value?.openMenu();
}

/**
 * Review selected dates and redirect to appropriate URL
 */
function onEndDateChange() {
  if (!startDate.value || !endDate.value) return;

  const start = startDate.value.toISOString().split('T')[0];
  const end = endDate.value.toISOString().split('T')[0];

  const baseURL = window.location.origin + '/whats-on/';
  if (start === end) {
    // Single date archive
    window.location = baseURL + start.replaceAll('-', '/');
  } else {
    // Date range archive
    window.location = baseURL + `?dates=${encodeURIComponent(`${start}|${end}`)}`;
  }
}

/** 
 * Preselect dates from data attributes if available 
 */
onMounted(() => {
  const el = document.getElementById('date-picker-app');
  if (!el) return;

  const preStart = el.dataset.preselectedStart;
  const preEnd = el.dataset.preselectedEnd;

  if (preStart) {
    const date = new Date(preStart);
    if (!isNaN(date.getTime())) {
      startDate.value = date;
    }
  }
  
  if (preEnd) {
    const date = new Date(preEnd);
    if (!isNaN(date.getTime())) {
      endDate.value = date;
    }
  }
});

</script>
